import React, { useState, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  Image,
  Text,
  HStack,
  VStack,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Flex,
  Button,
  useColorModeValue,
  useBreakpointValue,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Tag,
  Avatar,
  Divider,
  Tooltip
} from '@chakra-ui/react';
import {
  FaUserAlt,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaInfoCircle
} from 'react-icons/fa';
import { urlvideo } from '../services/urls';

// Set moment locale globally
moment.locale('ar-dz'); // Use Arabic locale for moment

const localizer = momentLocalizer(moment);

const CalendarComponent = ({ events = [], isLoading, error }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedEvent, setSelectedEvent] = useState(null);

  // All hooks must be called at the top level
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const calendarBg = useColorModeValue("white", "gray.800");
  const eventBgColor = useColorModeValue('#3182ce', '#2b6cb0');
  const modalBg = useColorModeValue("white", "gray.700");
  const descriptionBg = useColorModeValue('gray.100', 'gray.600');
  const cardBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "gray.200");

  const isMobile = useBreakpointValue({ base: true, md: false });
  const calendarHeight = useBreakpointValue({
    base: 'calc(100vh - 120px)',
    md: 'calc(100vh - 32px)'
  });

  // Memoize formatted events
  const formattedEvents = useMemo(() =>
    events.map(event => ({
      ...event,
      start: moment(event.start).toDate(),
      end: moment(event.end).toDate(),
    })),
    [events]
  );

  const EventCard = ({ event, onClick }) => {
    return (
      <Tooltip label="انقر للتفاصيل" placement="top" hasArrow>
        <Box
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          overflow="hidden"
          p={3}
          bg={cardBg}
          display="flex"
          alignItems="center"
          flexDirection={isMobile ? 'column' : 'row'}
          onClick={onClick}
          cursor="pointer"
          _hover={{
            transform: 'scale(1.02)',
            transition: 'transform 0.2s',
            boxShadow: 'md'
          }}
          boxShadow="sm"
          height="100%"
          dir="rtl"
        >
          <Avatar
            src={urlvideo + event.imageUrl}
            name={event.title}
            size="md"
            mb={isMobile ? 2 : 0}
            ml={isMobile ? 0 : 3} // Changed mr to ml for RTL
          />
          <Box textAlign={isMobile ? 'center' : 'right'} flex="1">
            <Text
              fontWeight="bold"
              color={textColor}
              noOfLines={1}
            >
              {event.title}
            </Text>
            <HStack spacing={2} mt={1} justifyContent={isMobile ? 'center' : 'flex-end'}> {/* Changed flex-start to flex-end for RTL */}
              <Icon as={FaUserAlt} color="gray.500" boxSize={3} />
              <Text fontSize="sm" color="gray.500" noOfLines={1}>
                {event.professor}
              </Text>
            </HStack>
            {!isMobile && (
              <HStack mt={2} spacing={1} justifyContent="flex-end"> {/* Added justifyContent for RTL */}
                <Badge colorScheme="blue" fontSize="xs">
                  {moment(event.start).format('h:mm A')}
                </Badge>
                {event.location && (
                  <Badge colorScheme="green" fontSize="xs" rightIcon={<FaMapMarkerAlt />}> {/* Changed leftIcon to rightIcon for RTL */}
                    {event.location}
                  </Badge>
                )}
              </HStack>
            )}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    onOpen();
  };

  const eventPropGetter = () => ({
    style: {
      backgroundColor: eventBgColor,
      borderRadius: '6px',
      border: 'none',
      color: 'white',
      padding: '2px 5px',
      fontSize: '0.85rem',
      cursor: 'pointer'
    },
  });

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="100vh" dir="rtl">
        <VStack spacing={4}>
          <Spinner size="xl" thickness="4px" color="blue.500" />
          <Text>جاري تحميل أحداث التقويم...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" height="100vh" p={4} dir="rtl">
        <Alert
          status="error"
          borderRadius="md"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          minHeight="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <Text mt={4} fontSize="lg" fontWeight="bold">
            فشل تحميل التقويم
          </Text>
          <Text mt={2} color="gray.600">
            {error.message || 'الرجاء المحاولة مرة أخرى لاحقًا'}
          </Text>
          <Button mt={4} colorScheme="red" onClick={() => window.location.reload()}>
            إعادة المحاولة
          </Button>
        </Alert>
      </Flex>
    );
  }

  return (
    <Box width="100%" minHeight="100vh" p={4} bg={bgColor} dir="rtl">
      <Box
        bg={calendarBg}
        borderRadius="xl"
        boxShadow="xl"
        height={calendarHeight}
        overflow="hidden"
        position="relative"
      >
        <Calendar
          localizer={localizer}
          events={formattedEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%', padding: '0 10px' }}
          components={{
            event: (eventProps) => (
              <EventCard
                event={eventProps.event}
                onClick={() => handleEventClick(eventProps.event)}
              />
            ),
          }}
          popup
          views={isMobile ? ['month'] : ['month', 'week', 'agenda']}
          step={15}
          showMultiDayTimes
          defaultView={isMobile ? 'month' : 'week'}
          defaultDate={new Date()}
          min={new Date(2024, 7, 15, 8, 0)}
          max={new Date(2024, 7, 15, 22, 0)}
          eventPropGetter={eventPropGetter}
          toolbar={true}
          selectable
          onSelectEvent={handleEventClick}
          messages={{
            next: 'التالي',
            previous: 'السابق',
            today: 'اليوم',
            month: 'شهر',
            week: 'أسبوع',
            day: 'يوم',
            agenda: 'جدول الأعمال',
            date: 'التاريخ',
            time: 'الوقت',
            event: 'الحدث',
            allDay: 'طوال اليوم',
            noEventsInRange: 'لا توجد أحداث في هذا النطاق.',
            showMore: total => `+ ${total} المزيد`,
          }}
        />
      </Box>

      {/* Event Details Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={isMobile ? "full" : "lg"}
        motionPreset="slideInBottom"
      >
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent bg={modalBg} dir="rtl">
          <ModalHeader borderBottomWidth="1px" pb={3}>
            <Flex align="center" flexDirection="row-reverse"> {/* Added flexDirection for RTL */}
              <Avatar
                src={urlvideo + selectedEvent?.imageUrl}
                name={selectedEvent?.title}
                size="md"
                ml={3} // Changed mr to ml for RTL
              />
              <Box textAlign="right"> {/* Added textAlign for RTL */}
                <Text fontSize="xl" fontWeight="bold">{selectedEvent?.title}</Text>
                <Text fontSize="sm" color="gray.500">{selectedEvent?.professor}</Text>
              </Box>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4}>
            {selectedEvent && (
              <VStack spacing={4} align="stretch">
                <HStack spacing={4} justifyContent="flex-end"> {/* Added justifyContent for RTL */}
                  <VStack align="end" spacing={1}> {/* Changed align to end for RTL */}
                    <Text fontSize="sm" color="gray.500">وقت البدء</Text>
                    <Tag colorScheme="blue">
                      <HStack>
                        <FaCalendarAlt />
                        <Text>{moment(selectedEvent.start).format('MMMM Do, YYYY')}</Text>
                      </HStack>
                    </Tag>
                    <Tag colorScheme="blue" mt={1}>
                      <HStack>
                        <FaClock />
                        <Text>{moment(selectedEvent.start).format('h:mm A')}</Text>
                      </HStack>
                    </Tag>
                  </VStack>

                  <VStack align="end" spacing={1}> {/* Changed align to end for RTL */}
                    <Text fontSize="sm" color="gray.500">وقت الانتهاء</Text>
                    <Tag colorScheme="red">
                      <HStack>
                        <FaCalendarAlt />
                        <Text>{moment(selectedEvent.end).format('MMMM Do, YYYY')}</Text>
                      </HStack>
                    </Tag>
                    <Tag colorScheme="red" mt={1}>
                      <HStack>
                        <FaClock />
                        <Text>{moment(selectedEvent.end).format('h:mm A')}</Text>
                      </HStack>
                    </Tag>
                  </VStack>
                </HStack>

                {selectedEvent.location && (
                  <Box textAlign="right"> {/* Added textAlign for RTL */}
                    <Text fontSize="sm" color="gray.500">الموقع</Text>
                    <Tag colorScheme="green">
                      <HStack>
                        <FaMapMarkerAlt />
                        <Text>{selectedEvent.location}</Text>
                      </HStack>
                    </Tag>
                  </Box>
                )}

                <Divider />

                {selectedEvent.description ? (
                  <Box textAlign="right"> {/* Added textAlign for RTL */}
                    <HStack mb={2} justifyContent="flex-end"> {/* Added justifyContent for RTL */}
                      <Icon as={FaInfoCircle} color="blue.500" />
                      <Text fontWeight="semibold">الوصف</Text>
                    </HStack>
                    <Text
                      whiteSpace="pre-line"
                      p={3}
                      bg={descriptionBg}
                      borderRadius="md"
                    >
                      {selectedEvent.description}
                    </Text>
                  </Box>
                ) : (
                  <Text color="gray.500" fontStyle="italic" textAlign="right">لا يوجد وصف مقدم</Text>
                )}
              </VStack>
            )}
          </ModalBody>
          <Flex justifyContent="flex-start" p={4} borderTopWidth="1px"> {/* Changed justifyContent to flex-start for RTL */}
            <Button
              colorScheme="blue"
              onClick={onClose}
              size={isMobile ? "md" : "sm"}
            >
              إغلاق
            </Button>
          </Flex>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CalendarComponent;



